import React, { useEffect, useRef, useState } from 'react';

import { ChevronDoubleRightIcon, FilterIcon } from '@vlabs/icons';
import { ExportTaskButton } from '@vlabs/monokit/features';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { useDebouncedSetFilters } from '@vlabs/shared/components/filters-panel/hooks';
import { routes } from '@vlabs/shared/navigation/routes';
import {
  Page, Table, Panel, Control, PageWithSidebar, Badge,
} from '@vlabs/uikit';
import cn from 'classnames';
import { throttle } from 'lodash';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import FilterForm from '../components/FilterFormArchive';
import { useEventTableColumns } from '../eventTableColumns';
import {
  selectEvents, selectEventsFilters, selectFilterCounts, selectIsTopMatchHiding,
} from './selectors';
import {
  updateFilters, updatePageSize, updatePage, setIsTopMatchHiding,
} from './store';
import './EventListPage.sass';

function EventListPage({
  filters = {},
  filterCounts = 0,
  setFilters = undefined,
  setPageSize = undefined,
  setPageIndex = undefined,
  eventsData = {},
  isTopMatchHiding = false,
  toggleTopMatchVisibility = undefined,
  includedAttributes = [],
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [isSidebarOpened, setIsSidebarOpened] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const formMethods = useForm({ defaultValues: filters });
  const lastScrollTop = useRef(0);

  const { watch, trigger } = formMethods;
  useDebouncedSetFilters({ trigger, watch }, setFilters);

  useEffect(() => {
    const tableBody = document.querySelector('.TableBody');
    if (!tableBody) return;

    const handleScroll = throttle(() => {
      const { scrollTop, scrollHeight, clientHeight } = tableBody;

      // если мы в самом низу таблицы — просто выходим
      if (scrollTop + clientHeight >= scrollHeight - 1) return;

      if (Math.abs(scrollTop - lastScrollTop.current) < 10) return;

      setIsHeaderVisible(scrollTop <= lastScrollTop.current);
      lastScrollTop.current = scrollTop;
    }, 100);

    tableBody.addEventListener('scroll', handleScroll);
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, []);

  const onEventCardClick = ({ event_id: eventId }) => {
    const eventLink = generatePath(routes.events.eventDetails, { eventId });
    history.push(eventLink);
  };

  const onTopMatchCardClick = ({ face_id, event_id }) => {
    if (face_id) {
      history.push(generatePath(routes.facePage, { faceId: face_id }));
    }
    if (event_id) {
      history.push(generatePath(routes.events.eventDetails, { eventId: event_id }));
    }
  };

  const columns = useEventTableColumns(onTopMatchCardClick, onEventCardClick, isTopMatchHiding, includedAttributes);
  const hasMap = includedAttributes.includes('map');

  const context = {
    ...formMethods,
    setFilters,
  };

  const actionButtons = (
    <div className="EventListPage__PageActions">
      <ExportTaskButton
        filters={filters}
        objectType="events"
      />
      <Control.Button
        className="EventListPage__FiltersButton"
        data-testid="filtersSidebar.toggleButton"
        icon={isSidebarOpened ? <ChevronDoubleRightIcon /> : <FilterIcon />}
        kind={isSidebarOpened ? 'secondary' : 'primary'}
        onClick={() => setIsSidebarOpened((prev) => !prev)}
        variant={isSidebarOpened ? 'filled' : 'outlined'}
      >
        {t('фильтры')}
        {filterCounts > 0 && <Badge value={filterCounts} />}
      </Control.Button>
    </div>
  );

  const topMatchSwitch = (
    <Control.Switch
      label={t('events:отображать наибольшее совпадение')}
      onChange={toggleTopMatchVisibility}
      value={!isTopMatchHiding}
    />
  );

  return (
    <FormProvider {...context}>
      <form style={{ flex: 1 }}>
        <PageWithSidebar
          className="FullHeightSidebar"
          isOpenRightSidebar={isSidebarOpened}
          rightSidebar={(
            <React.Fragment>
              <FiltersHeader
                isAdaptive
                onClick={() => setIsSidebarOpened((prev) => !prev)}
              />
              <FilterForm hasMap={hasMap} />
            </React.Fragment>
          )}
        >
          <div className={cn('EventListPage', { EventListPage__Header_hidden: !isHeaderVisible })}>
            <Page
              actions={actionButtons}
              className="EventListPage__Page"
              status={topMatchSwitch}
              title={t('events:page.archive_events.title')}
            />

            <div className="EventListPage__TableWrapper">
              <Panel className="EventListPage__Table_custom">
                <Table
                  columns={columns}
                  data={eventsData.data}
                  pageIndex={eventsData.pageIndex}
                  pageSize={eventsData.pageSize}
                  pageSizeOptions={eventsData.pageSizeOptions}
                  paginationType="offsetBased"
                  rowProps={(row) => ({ 'data-event-id': row.original?.event_id })}
                  setPageIndex={setPageIndex}
                  setPageSize={setPageSize}
                />
              </Panel>
            </div>
          </div>
        </PageWithSidebar>
      </form>
    </FormProvider>
  );
}

EventListPage.propTypes = {
  eventsData: PropTypes.objectOf(PropTypes.any),
  filters: PropTypes.objectOf(PropTypes.any),
  filterCounts: PropTypes.number,
  setFilters: PropTypes.func,
  setPageSize: PropTypes.func,
  setPageIndex: PropTypes.func,
  isTopMatchHiding: PropTypes.bool,
  toggleTopMatchVisibility: PropTypes.func,
  includedAttributes: PropTypes.arrayOf(PropTypes.string),
};

export default connect(
  (state) => ({
    eventsData: selectEvents(state),
    filters: selectEventsFilters(state),
    filterCounts: selectFilterCounts(state),
    isTopMatchHiding: selectIsTopMatchHiding(state),
  }),
  (dispatch) => ({
    setFilters: (filters) => dispatch(updateFilters({ filters })),
    setPageSize: (pageSize) => dispatch(updatePageSize({ pageSize })),
    setPageIndex: (pageIndex) => dispatch(updatePage({ pageIndex })),
    toggleTopMatchVisibility: () => dispatch(setIsTopMatchHiding()),
  }),
)(EventListPage);
